// libraries
import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { useFormContext } from 'react-hook-form'
import { Grid, IconButton, InputAdornment } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

// queries
import { useGetInventoryByCommodityQuery } from 'services/inventory'

// components
import Input from 'components/generic/form/Input'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'

// helpers and utils
import { toSnailCase } from 'utils/data-manipulation/common'
import { setDecimalPrecision } from 'utils/helpers'

const RegradeMaterialRow = ({ name, onClickDelete, deleteDisabled, disabled, setRowValues }) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const { defaultYardUm } = useSelector(state => state.settings)

  const [commodityId, setCommodityId] = useState()
  const [materialTypeId, setMaterialTypeId] = useState()
  const [materialWIP, setMaterialWIP] = useState()

  const materialWatch = watch(`${name}.material`)
  const quantityWatch = watch(`${name}.quantity`)

  useEffect(() => {
    if (!materialWatch?.materialTypeId) {
      setMaterialWIP(`0.0 ${defaultYardUm}`)
      setCommodityId('')
      setMaterialTypeId('')
      return
    }
    if (!materialWatch?.commodityName) return
    setCommodityId(toSnailCase(materialWatch?.commodityName))
    setMaterialTypeId(materialWatch?.materialTypeId || materialWatch?.id)
  }, [materialWatch])

  const { data: commodityInventoryData } = useGetInventoryByCommodityQuery(
    { commodityId },
    {
      skip: !commodityId,
      refetchOnMountOrArgChange: true,
    },
  )

  const materialInventory = useMemo(() => {
    if (!commodityInventoryData?.Items?.length) return {}
    return commodityInventoryData?.Items?.find(item => item?.materialTypeId === materialTypeId) || {}
  }, [materialTypeId, commodityInventoryData])

  useEffect(() => {
    if (!materialInventory) return
    const wipAmount = materialInventory?.wip?.amount || 0
    setMaterialWIP(`${setDecimalPrecision(wipAmount, { decimalPrecisionOverride: 2 })} ${defaultYardUm}`)

    const quantity = quantityWatch || 0
    const cost = materialInventory?.wipAvgCost?.amount || 0
    const precision = materialInventory?.wipAvgCost?.precision || 0
    const value = (quantity * cost) / 10 ** precision
    setRowValues(prevRows => ({
      ...prevRows,
      [name]: { value, quantity: quantityWatch },
    }))
  }, [materialInventory, quantityWatch])

  return (
    <Grid container mt={1} spacing={2} alignItems="center">
      <Grid container item xs={11} spacing={2}>
        <Grid item md={4} xs={12}>
          <MaterialAutocomplete
            label="Material"
            placeholder="Search by Material Name"
            variant="outlined"
            InputProps={{ name: `${name}.material` }}
            defaultValue={{
              commonName: materialWatch?.commonName,
              code: materialWatch?.code,
              materialTypeId: materialWatch?.id,
            }}
            onChange={material => {
              setValue(`${name}.material`, {
                ...material,
                materialTypeId: material?.id,
              })
            }}
            disabled={disabled}
            TextFieldProps={{
              error: name === 'from' && !!errors?.from,
              helperText: name === 'from' && errors?.from?.message,
            }}
          />
        </Grid>
        <Grid item md={4} xs={12}>
          <Input
            name={`${name}.quantity`}
            label="Quantity"
            defaultValue=""
            placeholder="-"
            control={control}
            disabled={disabled}
            InputProps={{
              inputComponent: InputFormat,
              endAdornment: <InputAdornment position="end">{defaultYardUm}</InputAdornment>,
              inputProps: {
                inputType: 'number',
                decimalScale: 1,
                allowNegative: false,
              },
            }}
          />
        </Grid>
        <Grid item md={4} xs={12} alignSelf="center">
          WIP: {materialWIP}
        </Grid>
      </Grid>
      <Grid item xs={1}>
        {onClickDelete && !deleteDisabled && (
          <IconButton aria-label="delete" onClick={onClickDelete}>
            <DeleteIcon />
          </IconButton>
        )}
      </Grid>
    </Grid>
  )
}

RegradeMaterialRow.propTypes = {
  name: PropTypes.string.isRequired,
  onClickDelete: PropTypes.func,
  disabled: PropTypes.bool,
  setRowValues: PropTypes.func,
  deleteDisabled: PropTypes.bool,
}

RegradeMaterialRow.defaultProps = {
  onClickDelete: null,
  disabled: false,
  setRowValues: () => {},
  deleteDisabled: false,
}

export default RegradeMaterialRow
